/**
 * Auth API: login, register.
 * Token is returned and should be stored in localStorage by the caller (e.g. AuthContext).
 */

import apiClient from './apiClient';

/** Map backend user (_id, first_name, ...) to frontend shape (id, name, ...) */
export const mapUserFromApi = (u) => {
  if (!u) return null;
  const id = u._id?.toString?.() ?? u.id;
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email;
  return {
    id,
    _id: u._id,
    email: u.email,
    name,
    firstName: u.first_name,
    lastName: u.last_name,
    number: u.number,
    role: u.role || 'user',
    createdAt: u.createdAt,
    mobile: u.number ?? u.mobile ?? '',
    pan: u.pan ?? '',
    experience: u.experience ?? '',
    risk: u.risk ?? '',
    broker: u.broker ?? '',
    profileImage: u.profile_image ?? u.profileImage ?? '',
    subscriptionPlan: u.subscription_plan ?? u.subscriptionPlan ?? '',
    purchasedCourseIds: Array.isArray(u.purchased_courses || u.purchasedCourseIds)
      ? (u.purchased_courses || u.purchasedCourseIds).map((x) => (x?._id ?? x).toString())
      : [],
    subscriptionFreeCourseIds: Array.isArray(u.subscription_free_course_ids || u.subscriptionFreeCourseIds)
      ? (u.subscription_free_course_ids || u.subscriptionFreeCourseIds).map((x) => String(x))
      : [],
  };
};

/**
 * Login
 * @param {{ email: string, password: string }}
 * @returns {{ success: boolean, token?: string, user?: object, message?: string }}
 */
export const login = async ({ email, password }) => {
  try {
    const { data } = await apiClient.post('/api/users/login', { email, password });
    return {
      success: true,
      token: data.token,
      user: mapUserFromApi(data.user),
    };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Login failed';
    return { success: false, message };
  }
};

/**
 * Register
 * @param {{ first_name: string, last_name: string, number?: string, email: string, password: string }}
 * @returns {{ success: boolean, token?: string, user?: object, message?: string }}
 */
export const register = async (payload) => {
  try {
    const { data } = await apiClient.post('/api/users/register', {
      first_name: payload.first_name,
      last_name: payload.last_name,
      number: payload.number || '',
      email: payload.email,
      password: payload.password,
    });
    return {
      success: true,
      token: data.token,
      user: mapUserFromApi(data.user),
    };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Registration failed';
    return { success: false, message };
  }
};

/**
 * Request registration OTP for an email.
 * @param {string} email
 */
export const requestRegisterOtp = async (email) => {
  try {
    const { data } = await apiClient.post('/api/users/register/request-otp', { email });
    return { success: true, message: data.message, expiresInMinutes: data.expiresInMinutes };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to send OTP';
    return { success: false, message };
  }
};

/**
 * Verify registration OTP for an email.
 * @param {string} email
 * @param {string} otp
 */
export const verifyRegisterOtp = async (email, otp) => {
  try {
    const { data } = await apiClient.post('/api/users/register/verify-otp', { email, otp });
    return { success: true, message: data.message };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to verify OTP';
    return { success: false, message };
  }
};

/**
 * Request password reset link for an email.
 * @param {string} email
 */
export const requestPasswordReset = async (email) => {
  try {
    const { data } = await apiClient.post('/api/users/forgot-password', { email });
    return { success: true, message: data.message };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to send reset link';
    return { success: false, message };
  }
};

/**
 * Request password reset OTP for an email.
 * @param {string} email
 */
export const requestPasswordResetOtp = async (email) => {
  try {
    const { data } = await apiClient.post('/api/users/forgot-password/request-otp', { email });
    return { success: true, message: data.message, expiresInMinutes: data.expiresInMinutes };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to send OTP';
    return { success: false, message };
  }
};

/**
 * Verify password reset OTP.
 * @param {string} email
 * @param {string} otp
 */
export const verifyPasswordResetOtp = async (email, otp) => {
  try {
    const { data } = await apiClient.post('/api/users/forgot-password/verify-otp', { email, otp });
    return { success: true, message: data.message, resetToken: data.resetToken };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to verify OTP';
    return { success: false, message };
  }
};

/**
 * Reset password after OTP verification.
 * @param {string} token
 * @param {string} password
 * @param {string} confirmPassword
 */
export const resetPasswordWithOtp = async (token, password, confirmPassword) => {
  try {
    const { data } = await apiClient.post('/api/users/forgot-password/reset', { token, password, confirmPassword });
    return { success: true, message: data.message };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to reset password';
    return { success: false, message };
  }
};

/**
 * Reset password using token from email.
 * @param {string} token
 * @param {string} password
 */
export const resetPasswordWithToken = async (token, password) => {
  try {
    const { data } = await apiClient.post('/api/users/reset-password', { token, password });
    return { success: true, message: data.message };
  } catch (err) {
    const message = err.response?.data?.message || err.message || 'Failed to reset password';
    return { success: false, message };
  }
};
